import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isNavEntryActive, sidebarGroups } from '../../navigation/routes';
import { Icon } from '../icons';
import type { SidebarState } from './useSidebarState';

interface SidebarProps {
  state: SidebarState;
}

/**
 * Seitenleiste wie in hr-tool/itsm: Marke als Schriftzug, gruppierte
 * Navigation ohne Icons, aktiver Eintrag schwarz. Die Gruppen klappen auf
 * und zu (Zustand in useSidebarState), unter 1024px ist die Leiste eine
 * Schublade, die ueber die Kopfzeile geoeffnet wird.
 */
const Sidebar: React.FC<SidebarProps> = ({ state }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const groups = useMemo(() => sidebarGroups(user), [user]);

  return (
    <aside id="app-sidebar" className="app-sidebar" aria-label="Seitenleiste">
      <div className="app-sidebar__header">
        <Link to="/" className="app-brand" aria-label="Intermal – Startseite">
          Intermal
        </Link>
        <button
          type="button"
          className="app-sidebar__close"
          onClick={state.closeMobile}
          aria-label="Menü schließen"
        >
          <Icon name="close" size={18} />
        </button>
      </div>

      <nav className="app-nav" aria-label="Hauptnavigation">
        {groups.map(({ group, items }) => {
          // Gruppen ohne Ueberschrift (Start) sind immer offen; die uebrigen
          // lassen sich zuklappen, damit die lange Liste ueberschaubar bleibt.
          const collapsible = Boolean(group.label) && group.collapsible;
          const open = !collapsible || state.isGroupOpen(group.id);
          const listId = `app-nav-group-${group.id}`;
          return (
            <div className="app-nav-group" key={group.id} data-open={open ? 'true' : 'false'}>
              {group.label && (
                collapsible ? (
                  <button
                    type="button"
                    className="app-nav-group__label app-nav-group__toggle"
                    aria-expanded={open}
                    aria-controls={listId}
                    onClick={() => state.toggleGroup(group.id)}
                  >
                    <span>{group.label}</span>
                    <span className="app-nav-group__count" aria-hidden="true">{items.length}</span>
                    <Icon name="chevron-down" size={14} className="app-nav-group__chevron" />
                  </button>
                ) : (
                  <p className="app-nav-group__label">{group.label}</p>
                )
              )}
              <div id={listId} hidden={!open}>
                {items.map((item) => {
                  // Aktiv auch auf Reitern und Unterseiten des Eintrags (isNavEntryActive).
                  // Der Erklaertext der Route ist der Tooltip (Paket 4 Schritt 2).
                  const active = isNavEntryActive(item, pathname);
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      onClick={state.closeMobile}
                      className={`app-nav-link${active ? ' is-active' : ''}`}
                      aria-current={active ? 'page' : undefined}
                      title={item.description}
                    >
                      {item.navLabel ?? item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default Sidebar;
